import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";

import { medicinesApi, medicineQueryKeys } from "@/shared/api/medicines";
import { mealTimingLabel } from "@/shared/format";
import { Input } from "@/components/ui/input";
import type { MealTiming } from "@/shared/types/medicine";
import type { EncounterPrescriptionItem } from "@/shared/types/encounter";

const NATIVE_SELECT =
  "h-9 rounded-md border border-input bg-background px-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

/** One-click "cách dùng" presets — fill dose + meal timing in a single choice. */
interface DosePattern {
  key: string;
  label: string;
  m: number;
  n: number;
  a: number;
  e: number;
  meal: MealTiming;
}
const DOSE_PATTERNS: DosePattern[] = [
  { key: "m1e1_after", label: "Sáng 1 – Tối 1, sau ăn", m: 1, n: 0, a: 0, e: 1, meal: "AFTER_MEAL" },
  { key: "m1_after", label: "Sáng 1, sau ăn", m: 1, n: 0, a: 0, e: 0, meal: "AFTER_MEAL" },
  { key: "m1n1e1_after", label: "Sáng 1 – Trưa 1 – Tối 1, sau ăn", m: 1, n: 1, a: 0, e: 1, meal: "AFTER_MEAL" },
  { key: "m1e1_before", label: "Sáng 1 – Tối 1, trước ăn", m: 1, n: 0, a: 0, e: 1, meal: "BEFORE_MEAL" },
  { key: "e1_bed", label: "Tối 1, trước khi ngủ", m: 0, n: 0, a: 0, e: 1, meal: "BEDTIME" },
  { key: "m2e2_after", label: "Sáng 2 – Tối 2, sau ăn", m: 2, n: 0, a: 0, e: 2, meal: "AFTER_MEAL" },
];
const DURATION_OPTS = [3, 5, 7, 10, 14, 30];
const perDayOf = (it: { doseMorning: number; doseNoon: number; doseAfternoon: number; doseEvening: number }) =>
  it.doseMorning + it.doseNoon + it.doseAfternoon + it.doseEvening;
const matchPattern = (it: EncounterPrescriptionItem): DosePattern | undefined =>
  DOSE_PATTERNS.find(
    (p) =>
      p.m === it.doseMorning &&
      p.n === it.doseNoon &&
      p.a === it.doseAfternoon &&
      p.e === it.doseEvening &&
      p.meal === (it.mealTiming ?? "AFTER_MEAL")
  );

/** Human-readable usage line for read-only / summary rendering. Copied verbatim (#18806). */
function describeUsage(it: EncounterPrescriptionItem): string {
  const slots = (["doseMorning", "doseNoon", "doseAfternoon", "doseEvening"] as const)
    .map((k, i) => (it[k] ? `${["Sáng", "Trưa", "Chiều", "Tối"][i]} ${it[k]}` : null))
    .filter(Boolean)
    .join(" – ");
  const meal = it.mealTiming ? mealTimingLabel[it.mealTiming].toLowerCase() : "";
  const qty = it.quantity ? ` · SL ${it.quantity} ${it.medicineUnit}` : "";
  return [slots, meal].filter(Boolean).join(", ") + qty || "Chưa nhập cách dùng";
}

/**
 * The prescribed drugs and how they are taken (TD-7b/1 slice 2, #18806).
 *
 * <p><b>Owns how a line is edited; owns neither the list nor when it is saved.</b> `items` comes
 * in and every change goes back out through a callback, because the `rx` list is shared with the
 * signing flow — {@code firstRxQtyError} reads it to refuse a signature. One list, one owner.
 *
 * <p>Two callbacks rather than one, and the split is behaviour rather than taste: typing into a
 * dose box is debounced ({@code onPatch}), while a discrete choice — a preset, a duration — saves
 * at once ({@code onPatchImmediate}). Collapsing them would either lose keystrokes or write on
 * every character.
 *
 * <p>What did move: the dose presets, the duration options, the days↔quantity arithmetic, the
 * per-row "custom" toggle and the route lookup. Nothing outside this component read any of them.
 */
export function PrescriptionLines({
  items,
  onPatch,
  onPatchImmediate,
  onRemove,
  qtyError,
  readOnly,
  allergyMarks,
}: {
  items: EncounterPrescriptionItem[];
  /** Debounced — for typing. */
  onPatch: (index: number, patch: Partial<EncounterPrescriptionItem>) => void;
  /** Saved at once — for discrete choices. */
  onPatchImmediate: (index: number, patch: Partial<EncounterPrescriptionItem>) => void;
  onRemove: (index: number) => void;
  /** True once signing has been refused for a missing quantity — highlights the offending rows. */
  qtyError: boolean;
  readOnly: boolean;
  /**
   * #19184 — medicineId → what it clashes with ("atorvastatin · Sốc phản vệ"). The general allergy
   * banner said the patient is allergic; it did not say WHICH line on the prescription is the
   * problem, so the card itself carries the mark.
   */
  allergyMarks?: ReadonlyMap<string, string>;
}) {
  /** Rows the doctor has switched to the manual Sáng/Trưa/Chiều/Tối grid. */
  const [rxCustom, setRxCustom] = useState<Set<string>>(new Set());
  /** Per-row duration; absent means "derive it from quantity ÷ doses-per-day". */
  const [rxDays, setRxDays] = useState<Record<string, number>>({});

  const routes = useQuery({
    queryKey: medicineQueryKeys.routes(),
    queryFn: () => medicinesApi.listRoutes(),
  });

  const daysFor = (it: EncounterPrescriptionItem): number => {
    if (rxDays[it.id] != null) return rxDays[it.id];
    const pd = perDayOf(it);
    if (pd > 0 && it.quantity > 0) return Math.max(1, Math.round(it.quantity / pd));
    return 7;
  };

  function applyPattern(i: number, it: EncounterPrescriptionItem, key: string) {
    if (key === "custom") {
      setRxCustom((s) => new Set(s).add(it.id));
      return;
    }
    setRxCustom((s) => {
      const n = new Set(s);
      n.delete(it.id);
      return n;
    });
    const p = DOSE_PATTERNS.find((x) => x.key === key);
    if (!p) return;
    const perDay = p.m + p.n + p.a + p.e;
    onPatchImmediate(i, {
      doseMorning: p.m,
      doseNoon: p.n,
      doseAfternoon: p.a,
      doseEvening: p.e,
      mealTiming: p.meal,
      route: it.route ?? "ORAL",
      quantity: perDay * daysFor(it) || it.quantity,
    });
  }

  function setDaysFor(i: number, it: EncounterPrescriptionItem, days: number) {
    setRxDays((prev) => ({ ...prev, [it.id]: days }));
    const perDay = perDayOf(it);
    if (perDay > 0) onPatchImmediate(i, { quantity: perDay * days });
  }

  return (
    <div className="mt-3 space-y-2">
      {items.map((it, i) => {
        const showCustom =
          rxCustom.has(it.id) ||
          (perDayOf(it) > 0 && !matchPattern(it));
        const selValue = showCustom
          ? "custom"
          : matchPattern(it)?.key ?? "";
        const qtyInvalid = Boolean(it.medicineId) && (!it.quantity || it.quantity <= 0);
        const allergyMark = it.medicineId ? allergyMarks?.get(it.medicineId) : undefined;
        return (
          <div
            key={it.id}
            className={`rounded-md border p-3 ${
              (qtyInvalid && qtyError) || allergyMark
                ? "border-destructive ring-1 ring-destructive"
                : ""
            }`}
            data-allergy={allergyMark ? "conflict" : undefined}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <b>
                  {it.medicineName}
                  {it.medicineStrength ? ` ${it.medicineStrength}` : ""}
                </b>
                {allergyMark && (
                  <div className="mt-0.5 text-xs font-semibold text-destructive" role="note">
                    Trùng dị ứng: {allergyMark}
                  </div>
                )}
              </div>
              {!readOnly && (
                <button
                  type="button"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => onRemove(i)}
                  aria-label="Xoá thuốc"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>

            {readOnly ? (
              <div className="mt-1 text-sm text-muted-foreground">
                {describeUsage(it)}
              </div>
            ) : (
              <>
                <div className="mt-2 flex flex-wrap items-end gap-2">
                  <select
                    className={`${NATIVE_SELECT} flex-1 min-w-[12rem]`}
                    value={selValue}
                    onChange={(ev) => applyPattern(i, it, ev.target.value)}
                  >
                    <option value="">— Chọn cách dùng —</option>
                    {DOSE_PATTERNS.map((p) => (
                      <option key={p.key} value={p.key}>
                        {p.label}
                      </option>
                    ))}
                    <option value="custom">Tùy chỉnh…</option>
                  </select>
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span>Số ngày</span>
                    <select
                      className={`${NATIVE_SELECT} w-20`}
                      value={daysFor(it)}
                      onChange={(ev) =>
                        setDaysFor(i, it, Number(ev.target.value))
                      }
                    >
                      {DURATION_OPTS.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                  </label>
                  <span className="text-sm">
                    SL <b>{it.quantity}</b> {it.medicineUnit}
                  </span>
                </div>

                {showCustom && (
                  <>
                    <div className="mt-2 grid grid-cols-5 gap-2">
                      {(
                        [
                          "doseMorning",
                          "doseNoon",
                          "doseAfternoon",
                          "doseEvening",
                        ] as const
                      ).map((k, ki) => (
                        <div key={k} className="space-y-1">
                          <span className="text-[11px] text-muted-foreground">
                            {["Sáng", "Trưa", "Chiều", "Tối"][ki]}
                          </span>
                          <Input
                            value={it[k]}
                            inputMode="decimal"
                            onChange={(ev) =>
                              onPatch(i, {
                                [k]: Number(ev.target.value) || 0,
                              } as Partial<EncounterPrescriptionItem>)
                            }
                          />
                        </div>
                      ))}
                      <div className="space-y-1">
                        <span className="text-[11px] text-muted-foreground">
                          SL
                        </span>
                        <Input
                          value={it.quantity}
                          inputMode="numeric"
                          onChange={(ev) =>
                            onPatch(i, {
                              quantity: Number(ev.target.value) || 0,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <select
                        className={NATIVE_SELECT}
                        value={it.route ?? ""}
                        onChange={(ev) =>
                          onPatchImmediate(i, {
                            route: ev.target.value || null,
                          })
                        }
                      >
                        <option value="">Đường dùng…</option>
                        {(routes.data ?? []).map((r) => (
                          <option key={r.code} value={r.code}>
                            {r.labelVi}
                          </option>
                        ))}
                      </select>
                      <select
                        className={NATIVE_SELECT}
                        value={it.mealTiming ?? ""}
                        onChange={(ev) =>
                          onPatchImmediate(i, {
                            mealTiming:
                              (ev.target.value as MealTiming) || null,
                          })
                        }
                      >
                        <option value="">Thời điểm…</option>
                        {(Object.keys(mealTimingLabel) as MealTiming[]).map(
                          (m) => (
                            <option key={m} value={m}>
                              {mealTimingLabel[m]}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        );
      })}
      {items.length === 0 && (
        <p className="text-sm text-muted-foreground">Chưa kê thuốc.</p>
      )}
    </div>
  );
}
